"use client"

import { db } from "@/lib/instantdb/client"

const clientDb = db as any
const APP_ID = process.env.NEXT_PUBLIC_INSTANTDB_APP_ID || ""
const IS_MOCK_MODE = !APP_ID || APP_ID === "placeholder_app_id"

export const TIMEZONE_OPTIONS = [
  { label: "New York (EST)", value: "America/New_York" },
  { label: "Chicago (CST)", value: "America/Chicago" },
  { label: "Los Angeles (PST)", value: "America/Los_Angeles" },
  { label: "London (GMT)", value: "Europe/London" },
  { label: "Lagos (WAT)", value: "Africa/Lagos" },
  { label: "Dubai (GST)", value: "Asia/Dubai" },
  { label: "Singapore (SGT)", value: "Asia/Singapore" },
  { label: "Sydney (AEST)", value: "Australia/Sydney" },
] as const

export const TRADING_STYLE_OPTIONS = [
  {
    id: "day",
    title: "Day trading",
    description: "Fast entries, same-day exits.",
  },
  {
    id: "swing",
    title: "Swing trading",
    description: "Multi-day moves, calmer pace.",
  },
  {
    id: "investor",
    title: "Position / investing",
    description: "Longer conviction plays.",
  },
  {
    id: "copy",
    title: "Copy / guided",
    description: "Mirror mentors while you learn.",
  },
] as const

export type StudentPreferences = {
  name?: string
  timezone?: string
  tradingStyle?: string // Legacy single style (for backward compatibility)
  tradingModes?: string[] // Array of enabled trading modes
  currentTradingMode?: string // Currently active trading mode
  completedAt?: number | null
}

const SETTINGS_KEY = "student-settings"

type StoredSettings = Record<string, any>

const readStoredSettings = (): StoredSettings => {
  if (typeof window === "undefined") return {}
  try {
    const raw = localStorage.getItem(SETTINGS_KEY)
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

const writeStoredSettings = (
  next: StoredSettings,
  { broadcast = true }: { broadcast?: boolean } = {}
) => {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(next))
    if (broadcast) {
      window.dispatchEvent(new Event("student-settings-updated"))
    }
  } catch {
    /* ignore write failures */
  }
}

const normalizePreferenceUpdates = (partial: StudentPreferences) => {
  const normalized: StoredSettings = {}
  if (typeof partial.name === "string") {
    normalized.name = partial.name
    normalized.alias = partial.name
  }
  if (partial.timezone) {
    normalized.timezone = partial.timezone
  }
  if (partial.tradingStyle) {
    normalized.tradingStyle = partial.tradingStyle
  }
  if (Array.isArray(partial.tradingModes)) {
    normalized.tradingModes = partial.tradingModes
  }
  if (partial.currentTradingMode) {
    normalized.currentTradingMode = partial.currentTradingMode
  }
  if (typeof partial.completedAt !== "undefined") {
    normalized.preferencesCompletedAt = partial.completedAt
  }
  return normalized
}

const setSessionTimezone = (timezone?: string) => {
  if (typeof window === "undefined" || !timezone) return
  try {
    sessionStorage.setItem("user-timezone", timezone)
    localStorage.setItem("user-timezone", timezone)
  } catch {
    /* ignore storage failures */
  }
}

export const getStoredPreferences = (): StudentPreferences => {
  const settings = readStoredSettings()
  // Migrate legacy single tradingStyle to new system
  const legacyStyle = settings.tradingStyle ?? settings.style ?? ""
  const tradingModes = settings.tradingModes ?? (legacyStyle ? [legacyStyle] : [])
  const currentMode = settings.currentTradingMode ?? legacyStyle ?? (tradingModes.length > 0 ? tradingModes[0] : "")
  
  return {
    name: settings.name ?? settings.alias ?? "",
    timezone: settings.timezone ?? "",
    tradingStyle: legacyStyle, // Keep for backward compatibility
    tradingModes: tradingModes.length > 0 ? tradingModes : undefined,
    currentTradingMode: currentMode || undefined,
    completedAt: settings.preferencesCompletedAt ?? null,
  }
}

export const hasCompletedPreferences = (prefs?: StudentPreferences) => {
  const data = prefs ?? getStoredPreferences()
  return Boolean(data.completedAt)
}

const updateLocalPreferences = (
  partial: StudentPreferences,
  options?: { broadcast?: boolean }
) => {
  if (typeof window === "undefined") return getStoredPreferences()
  const existing = readStoredSettings()
  const normalized = normalizePreferenceUpdates(partial)
  const next = { ...existing, ...normalized }
  writeStoredSettings(next, options)
  if (partial.timezone) {
    setSessionTimezone(partial.timezone)
  }
  return getStoredPreferences()
}

const mapPreferencesToUserUpdate = (preferences: StudentPreferences) => ({
  preferredName: preferences.name || undefined,
  preferredTimezone: preferences.timezone || undefined,
  preferredStyle: preferences.currentTradingMode || preferences.tradingStyle || undefined,
  preferredTradingModes: preferences.tradingModes || undefined,
  currentTradingMode: preferences.currentTradingMode || undefined,
  preferencesCompletedAt: preferences.completedAt || undefined,
})

export const syncPreferencesToInstantDB = async (
  userId: string,
  preferences: StudentPreferences
) => {
  if (IS_MOCK_MODE) return
  try {
    await clientDb.transact([
      clientDb.tx["$users"][userId].update(mapPreferencesToUserUpdate(preferences)),
    ])
  } catch (error) {
    const message = (error as any)?.message || String(error)
    if (message.includes("does not exist in schema")) {
      console.warn("InstantDB schema missing ($users); skipping preference sync.")
      return
    }
    console.error("Failed to sync preferences to InstantDB", error)
  }
}

export const saveStudentPreferences = async (
  partial: StudentPreferences,
  options?: { userId?: string; broadcast?: boolean }
) => {
  const merged = updateLocalPreferences(partial, {
    broadcast: options?.broadcast,
  })
  if (options?.userId) {
    await syncPreferencesToInstantDB(options.userId, merged)
  }
  return merged
}

export const fetchPreferencesFromInstantDB = async (userId: string) => {
  if (IS_MOCK_MODE) {
    return getStoredPreferences()
  }
  try {
    const { data } = await clientDb.query({
      users: {
        $: {
          where: { id: userId },
          limit: 1,
        },
      },
    })
    const user = data?.users?.[0]
    if (!user) {
      return null
    }
    const preferences: StudentPreferences = {
      name: user.preferredName ?? user.name ?? "",
      timezone: user.preferredTimezone ?? "",
      tradingStyle: user.preferredStyle ?? "",
      tradingModes: user.preferredTradingModes ?? (user.preferredStyle ? [user.preferredStyle] : undefined),
      currentTradingMode: user.currentTradingMode ?? user.preferredStyle ?? "",
      completedAt: user.preferencesCompletedAt ?? null,
    }
    updateLocalPreferences(preferences)
    return preferences
  } catch (error) {
    const message = (error as any)?.message || String(error)
    if (message.includes("does not exist in schema")) {
      console.warn("InstantDB schema missing ($users); returning local preferences")
      return getStoredPreferences()
    }
    console.error("Failed to fetch preferences from InstantDB", error)
    return null
  }
}


